import { m } from '~/paraglide/messages'

export type CardState = 'baking' | 'finish' | 'opened' | 'missed' | 'up-next'

export type ChallengeStatus =
  | 'not_started'
  | 'active'
  | 'completed'
  | 'failed'
  | 'reward_delivered'

export type ChallengeProgressRow = {
  id: number
  challenge_difficulty: { requires_subscription: boolean }
  available_at: string
  due_at: string
  progress: number
  target: number
  status: ChallengeStatus
  reward_delivered_at: string | null
}

export type TierState = {
  state: CardState
  progress: number
  target: number
  ringPercent: number
  progressId: number | null
}

export type DayCard = {
  day: number
  date: Date
  isToday: boolean
  isPast: boolean
  isFuture: boolean
  free: TierState
  plus: TierState
  countdown: string
}

export type DeriveDayCardsInput = {
  progresses: ChallengeProgressRow[]
  now: Date
  eventStartDate: Date
  eventEndDate: Date
}

const DAY_MS = 24 * 60 * 60 * 1000

function dayIndex(start: Date, when: Date): number {
  return Math.floor((when.getTime() - start.getTime()) / DAY_MS)
}

function buildTier(
  row: ChallengeProgressRow,
  rowDayIndex: number,
  todayIndex: number,
): TierState {
  const ringPercent = Math.min(
    100,
    Math.max(0, Math.round((row.progress / row.target) * 100)),
  )
  const isToday = rowDayIndex === todayIndex
  const isPast = rowDayIndex < todayIndex
  const completed = row.status === 'completed' || row.progress >= row.target
  const delivered =
    row.status === 'reward_delivered' || row.reward_delivered_at !== null

  let state: CardState
  if (delivered) {
    state = 'opened'
  } else if (completed && (isToday || isPast)) {
    // Today's completed-but-unclaimed and any past-day finish state both
    // surface the Open CTA. Past day stays claimable until the user opens it.
    state = 'finish'
  } else if (isPast) {
    state = 'missed'
  } else {
    state = 'baking'
  }

  const adjustedRing =
    state === 'finish' || state === 'opened' ? 100 : ringPercent

  return {
    state,
    progress: row.progress,
    target: row.target,
    ringPercent: adjustedRing,
    progressId: row.id,
  }
}

function emptyTier(rowDayIndex: number, todayIndex: number): TierState {
  return {
    state: rowDayIndex < todayIndex ? 'missed' : 'up-next',
    progress: 0,
    target: 60,
    ringPercent: 0,
    progressId: null,
  }
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS

function formatCountdown(
  dayIndexValue: number,
  todayIndex: number,
  dayStart: Date,
  dayEnd: Date,
  now: Date,
): string {
  if (dayIndexValue < todayIndex) return m.ended_text()
  if (dayIndexValue === todayIndex) {
    const diff = Math.max(0, dayEnd.getTime() - now.getTime())
    return m.event_state_time_left({
      hour: Math.floor(diff / HOUR_MS),
      minute: Math.floor((diff % HOUR_MS) / MINUTE_MS),
    })
  }
  const diff = Math.max(0, dayStart.getTime() - now.getTime())
  if (diff >= DAY_MS) {
    return m.event_state_start_time_d({ day: Math.floor(diff / DAY_MS) })
  }
  if (diff >= HOUR_MS) {
    return m.event_state_start_time_h_m({
      hour: Math.floor(diff / HOUR_MS),
      min: Math.floor((diff % HOUR_MS) / MINUTE_MS),
    })
  }
  return m.event_state_start_time_m({ min: Math.floor(diff / MINUTE_MS) })
}

export function deriveDayCards(input: DeriveDayCardsInput): DayCard[] {
  const { progresses, now, eventStartDate, eventEndDate } = input
  const totalDays =
    Math.floor((eventEndDate.getTime() - eventStartDate.getTime()) / DAY_MS) + 1
  const todayIndex = dayIndex(eventStartDate, now)

  const byDay: Record<
    number,
    { free?: ChallengeProgressRow; plus?: ChallengeProgressRow }
  > = {}
  for (const row of progresses) {
    const i = dayIndex(eventStartDate, new Date(row.available_at))
    if (i < 0 || i >= totalDays) continue
    byDay[i] ??= {}
    if (row.challenge_difficulty.requires_subscription) {
      byDay[i].plus = row
    } else {
      byDay[i].free = row
    }
  }

  const cards: DayCard[] = []
  for (let i = 0; i < totalDays; i++) {
    const date = new Date(eventStartDate.getTime() + i * DAY_MS)
    const isToday = i === todayIndex
    const pair = byDay[i] ?? {}
    const free = pair.free
      ? buildTier(pair.free, i, todayIndex)
      : emptyTier(i, todayIndex)
    const plus = pair.plus
      ? buildTier(pair.plus, i, todayIndex)
      : emptyTier(i, todayIndex)
    const dayStart = date
    const dayEnd = new Date(date.getTime() + DAY_MS)
    cards.push({
      day: i + 1,
      date,
      isToday,
      isPast: i < todayIndex,
      isFuture: i > todayIndex,
      free,
      plus,
      countdown: formatCountdown(i, todayIndex, dayStart, dayEnd, now),
    })
  }
  return cards
}
